import { useState, useRef, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { MapPin, Search, Keyboard } from "lucide-react";

export function AddressAutocomplete({ value, onChange, placeholder, className, name, dataTestId }) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isManual, setIsManual] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (text) => {
    if (!text || text.length < 3 || isManual) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(text)}&countrycodes=us&limit=5&addressdetails=1`,
        { headers: { "Accept-Language": "en" } }
      );
      const data = await res.json();
      const formatted = data.map((item) => ({
        display: item.display_name,
        short: [
          item.address?.house_number,
          item.address?.road,
          item.address?.city || item.address?.town || item.address?.village,
          item.address?.state,
          item.address?.postcode
        ].filter(Boolean).join(", ")
      }));
      setSuggestions(formatted);
      setShowDropdown(formatted.length > 0);
    } catch {
      setSuggestions([]);
    } finally { setLoading(false); }
  }, [isManual]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!isManual) {
      debounceRef.current = setTimeout(() => fetchSuggestions(val), 400);
    }
  };

  const selectSuggestion = (suggestion) => {
    setQuery(suggestion.short || suggestion.display);
    onChange(suggestion.short || suggestion.display);
    setShowDropdown(false);
    setSuggestions([]);
  };

  const toggleManual = () => {
    setIsManual(!isManual);
    setSuggestions([]);
    setShowDropdown(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <Input
          value={query}
          onChange={handleInputChange}
          onFocus={() => { if (suggestions.length > 0 && !isManual) setShowDropdown(true); }}
          placeholder={isManual ? "Type address manually..." : (placeholder || "Start typing an address...")}
          className={`pl-9 pr-10 ${className || ""}`}
          name={name}
          data-testid={dataTestId}
          autoComplete="off"
        />
        <button
          type="button"
          onClick={toggleManual}
          className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors ${isManual ? "text-blue-600 bg-blue-50" : "text-muted-foreground hover:text-gray-700"}`}
          title={isManual ? "Switch to address lookup" : "Switch to manual input"}
        >
          {isManual ? <Search className="w-4 h-4" /> : <Keyboard className="w-4 h-4" />}
        </button>
      </div>

      {loading && (
        <div className="absolute right-10 top-1/2 -translate-y-1/2">
          <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto" data-testid="address-suggestions">
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => selectSuggestion(s)}
              className="w-full text-left px-3 py-2.5 hover:bg-blue-50 flex items-start gap-2 border-b border-gray-50 last:border-0 transition-colors"
              data-testid={`address-suggestion-${i}`}
            >
              <MapPin className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-800">{s.short}</p>
                <p className="text-xs text-muted-foreground truncate">{s.display}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {isManual && (
        <p className="text-[10px] text-blue-500 mt-0.5 flex items-center gap-1">
          <Keyboard className="w-3 h-3" /> Manual input mode — click the search icon to enable address lookup
        </p>
      )}
    </div>
  );
}
